import { Component, OnInit, ChangeDetectorRef, AfterViewInit, ChangeDetectionStrategy, ViewChild, inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AlertController, ToastController, IonPopover, PopoverController } from '@ionic/angular';
import { SERVER_URL } from '../../environments/environment';
import { PartnerTokenService } from '../_core/services/partner-token.service';
import { PublisherService } from '../admin/admin-publishers/publisher.service';
import { AuthService } from '../auth/auth.service';
import { FilterPopoverComponent, FilterOption } from '../_shared/components/filter-popover/filter-popover.component';
import { NavigationService } from '../_core/services/navigation.service';

import { firstValueFrom } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { ToastService } from '../_core/services/toast.service';

@Component({
  selector: 'app-generate-token',
  templateUrl: './generate-token.component.html',
  styleUrls: ['./generate-token.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class GenerateTokenComponent implements OnInit, AfterViewInit {
  @ViewChild('datePopover') datePopover: IonPopover;
  private translateService = inject(TranslateService);

  tokenForm: FormGroup;
  isLoading = false;
  isLoadingPartners = false;
  isCustomPartner = false;

  // Dynamic partner names from API
  partnerNames: string[] = [];

  // Filter properties for admin users
  selectedPublisher = '';
  selectedPublisherId = ''; // Store the publisher ID for API calls
  filteredPartnerNames: Array<{
    _id: string;
    partnerName: string;
    autoSubscription: boolean;
  }> = [];

  // Publisher data from API
  publishers: Array<{
    _id: string;
    publisherId: string;
    publisherName: string;
    publisher: string;
    partnerSubscription: Array<{
      partnerName: string;
      autoSubscription: boolean;
      _id: string;
    }>;
  }> = [];

  constructor(
    private fb: FormBuilder,
    private alertController: AlertController,
    private toastController: ToastService,
    private cdr: ChangeDetectorRef,
    private partnerTokenService: PartnerTokenService,
    private publisherService: PublisherService,
    public authService: AuthService,
    private popoverController: PopoverController,
    private navigationService: NavigationService
  ) {
    // Initialize the form in constructor to avoid NG01052 error
    this.tokenForm = this.fb.group({
      partnerName: ['', [Validators.required, this.noSpacesValidator()]],
      expiryDate: ['', Validators.required]
    });
  }

  // Custom validator to prevent spaces in partner name
  private noSpacesValidator() {
    return (control: any) => {
      const value = control.value;
      if (value && value.includes(' ')) {
        return { hasSpaces: true };
      }
      return null;
    };
  }

  ngOnInit() {
    // Load partner names from API
    this.loadPartnerNames();

    // Set default expiry date to 30 days from now
    const defaultDate = new Date();
    defaultDate.setDate(defaultDate.getDate() + 30);
    this.tokenForm.patchValue({ expiryDate: defaultDate.toISOString() });
  }

  // Check if user has admin role (500)
  get isAdmin(): boolean {
    const user = this.authService.authUserSignal();
    return user?.role?.value === 500;
  }

  ngAfterViewInit() {
    // Alternative approach: set default value after view is initialized
    // This should avoid the ExpressionChangedAfterItHasBeenCheckedError
  }

  togglePartnerInput() {
    this.isCustomPartner = !this.isCustomPartner;
    // Clear the form control when switching modes
    this.tokenForm.patchValue({ partnerName: '' });

    // Keep the publisher selection when switching to custom mode
    // The selectedPublisherId will be preserved and used in the API call
    this.cdr.detectChanges();
  }

  async openDatePicker() {
    if (this.datePopover) {
      await this.datePopover.present();
    }
  }

  onDateChange(event: any) {
    const date = new Date(event.detail.value);

    this.tokenForm.patchValue({ expiryDate: date.toISOString() });
    this.cdr.detectChanges();

    // Close the popover after selection
    if (this.datePopover) {
      this.datePopover.dismiss();
    }
  }

  async loadPartnerNames() {
    this.isLoadingPartners = true;
    this.cdr.detectChanges();

    try {
      if (this.isAdmin) {
        // For admin users, load publishers from /api/publisher/
        const publisherResponse = await Promise.race([
          firstValueFrom(this.publisherService.getPublishers()),
          new Promise((_, reject) =>
            setTimeout(() => reject(new Error('Request timeout')), 10000)
          )
        ]);

        if (publisherResponse && publisherResponse.data && Array.isArray(publisherResponse.data)) {
          this.publishers = publisherResponse.data;

          // Initialize filtered partner names
          this.updateFilteredPartnerNames();

          // For backward compatibility, also populate partnerNames with all partner names
          const allPartnerNames: string[] = [];
          this.publishers.forEach(publisher => {
            if (publisher.partnerSubscription) {
              publisher.partnerSubscription.forEach(partner => {
                if (!allPartnerNames.includes(partner.partnerName)) {
                  allPartnerNames.push(partner.partnerName);
                }
              });
            }
          });
          this.partnerNames = allPartnerNames;
        } else {
          this.publishers = [];
          this.partnerNames = [];
        }
      } else {
        // For non-admin users, load from the existing API
        const response = await Promise.race([
          firstValueFrom(this.publisherService.getAllSubscriptionPartner()),
          new Promise((_, reject) =>
            setTimeout(() => reject(new Error('Request timeout')), 10000)
          )
        ]);

        if (response && response.data) {
          let partnerData = response.data;

          if (Array.isArray(partnerData)) {
            this.partnerNames = partnerData;
          } else if (partnerData.partners && Array.isArray(partnerData.partners)) {
            this.partnerNames = partnerData.partners;
          } else if (typeof partnerData === 'object') {
            this.partnerNames = Object.keys(partnerData);
          } else {
            this.partnerNames = [];
          }
        } else if (response && Array.isArray(response)) {
          this.partnerNames = response;
        } else {
          this.partnerNames = [];
        }

      }
    } catch (error) {
      this.partnerNames = [];
      this.publishers = [];
      // Show error toast
      this.toastController.presentDangerToast('Failed to load partner names. Please try again.');
    } finally {
      this.isLoadingPartners = false;
      this.cdr.detectChanges();
    }
  }



  onPartnerNameInput(event: any) {
    const value = event.target.value;
    // You can add validation or filtering logic here
  }

  async generateToken() {
    if (this.tokenForm.invalid) {
      const alert = await this.alertController.create({
        header: 'Validation Error',
        message: 'Please fill in all required fields.',
        buttons: ['OK']
      });
      await alert.present();
      return;
    }

    this.isLoading = true;

    // Get the expiry date from form
    const expiryDate = this.tokenForm.value.expiryDate;

    if (!expiryDate) {
      throw new Error('Expiry date is required');
    }

    // Parse the date properly
    let date: Date;
    if (typeof expiryDate === 'string') {
      // If it's already an ISO string, parse it directly
      if (expiryDate.includes('T')) {
        date = new Date(expiryDate);
      } else {
        // If it's a date string without time, add time
        date = new Date(expiryDate + 'T23:59:59');
      }
    } else {
      // If it's a Date object
      date = new Date(expiryDate);
    }

    if (isNaN(date.getTime())) {
      throw new Error('Invalid date format');
    }

    // Set to end of day
    date.setHours(23, 59, 59, 999);

    // Get the publisher ID - either from selected partner or from publisher filter
    let publisherId = this.selectedPublisherId;

    // If no publisher ID is set but we have a selected publisher, use that
    if (!publisherId && this.selectedPublisher) {
      publisherId = this.selectedPublisher;
    }

    const payload = {
      partnerName: this.tokenForm.value.partnerName,
      expiryDate: date.toISOString(),
      publisher: publisherId // Include the publisher ID
    };

    try {
      const response = await firstValueFrom(this.partnerTokenService.generateToken(payload));
      this.toastController.presentToast(response?.message || 'Token generated successfully!');     

      // Reset form after successful generation
      this.tokenForm.reset();
      this.ngOnInit(); // Reset to default values

    } catch (error) {

      let errorMessage = 'Failed to generate token. Please try again.';
      if (error instanceof Error) {
        errorMessage = error.message;
      }

      const alert = await this.alertController.create({
        header: 'Error',
        message: errorMessage,
        buttons: ['OK']
      });
      await alert.present();
    } finally {
      this.isLoading = false;
    }
  }



  getMinDate(): string {
    // Minimum date is today    
    return new Date().toISOString();
  }

  getMaxDate(): string {
    // Maximum date is 1 year from now
    const maxDate = new Date();
    maxDate.setFullYear(maxDate.getFullYear() + 1);
    return maxDate.toISOString();
  }

  getFormattedDate(): string {
    const date = this.tokenForm.get('expiryDate')?.value;
    if (date) {
      const dateObj = new Date(date);
      // Format as yyyy-mm-dd
      const year = dateObj.getFullYear();
      const month = String(dateObj.getMonth() + 1).padStart(2, '0');
      const day = String(dateObj.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
    return '';
  }

  updateFilteredPartnerNames() {
    if (!this.isAdmin) {
      // For non-admin users, use partnerNames from the API
      this.filteredPartnerNames = this.partnerNames.map(partnerName => ({
        _id: partnerName, // Use partnerName as _id for non-admin users
        partnerName: partnerName,
        autoSubscription: false
      }));
      return;
    }

    if (!this.selectedPublisher) {
      // If no publisher is selected, show all partners from all publishers
      const allPartners: Array<{
        _id: string;
        partnerName: string;
        autoSubscription: boolean;
      }> = [];

      this.publishers.forEach(publisher => {
        if (publisher.partnerSubscription) {
          publisher.partnerSubscription.forEach(partner => {
            allPartners.push({
              _id: partner._id,
              partnerName: partner.partnerName,
              autoSubscription: partner.autoSubscription
            });
          });
        }
      });

      this.filteredPartnerNames = allPartners;
    } else {
      // Filter partners by selected publisher
      const selectedPublisherData = this.publishers.find(p => p.publisher === this.selectedPublisher);
      if (selectedPublisherData && selectedPublisherData.partnerSubscription) {
        this.filteredPartnerNames = selectedPublisherData.partnerSubscription.map(partner => ({
          _id: partner._id,
          partnerName: partner.partnerName,
          autoSubscription: partner.autoSubscription
        }));
      } else {
        this.filteredPartnerNames = [];
      }
    }
  }

  onPublisherFilterChange() {
    this.tokenForm.patchValue({ partnerName: '' }); // Reset partner selection when publisher changes
    this.updateFilteredPartnerNames();
    this.cdr.markForCheck();
  }

  async openPublisherFilter(event: any) {
    if (!this.isAdmin) return;

    const options: FilterOption[] = [
      // { label: 'All Publisher', value: '' }
    ];

    // Add publisher options
    this.publishers.forEach(publisher => {
      options.push({
        label: publisher.publisherName,
        value: publisher.publisher
      });
    });

    const popover = await this.popoverController.create({
      component: FilterPopoverComponent,
      componentProps: {
        title: this.translateService.instant('Select Publisher'),
        options: options,
        selectedValue: this.selectedPublisher
      },
      event: event,
      translucent: true,
      cssClass: 'publisher-filter-popover',
      alignment: 'center',
      side: 'bottom',
      size: 'auto',
      backdropDismiss: true,
      showBackdrop: true
    });

    await popover.present();

    const { data } = await popover.onDidDismiss();
    if (data !== undefined) {
      this.selectedPublisher = data;
      this.onPublisherFilterChange();
    }
  }

  async openPartnerNameFilter(event: any) {
    const options: FilterOption[] = [
      // { label: this.isAdmin ? 'All Partner Names' : 'All Partners', value: '' }
    ];

    if (this.isAdmin) {
      // For admin users, use filtered partner names based on selected publisher
      this.filteredPartnerNames.forEach(partner => {
        options.push({
          label: partner.partnerName,
          value: partner.partnerName // Store partnerName instead of _id
        });
      });
    } else {
      // For non-admin users, use partnerNames from the API
      this.partnerNames.forEach(partnerName => {
        options.push({
          label: partnerName,
          value: partnerName
        });
      });
    }

    const popover = await this.popoverController.create({
      component: FilterPopoverComponent,
      componentProps: {
        title: this.isAdmin ? 'Select Partner Name' : 'Select Partner',
        options: options,
        selectedValue: this.tokenForm.get('partnerName')?.value || ''
      },
      event: event,
      translucent: true,
      cssClass: 'partner-filter-popover',
      alignment: 'center',
      side: 'bottom',
      size: 'auto',
      backdropDismiss: true,
      showBackdrop: true
    });

    await popover.present();

    const { data } = await popover.onDidDismiss();
    if (data !== undefined) {
      this.tokenForm.patchValue({ partnerName: data });

      // Store the publisher ID when a partner is selected
      if (this.isAdmin) {
        const selectedPartner = this.filteredPartnerNames.find(p => p.partnerName === data);
        if (selectedPartner) {
          // Find the publisher that contains this partner
          const publisher = this.publishers.find(p =>
            p.partnerSubscription?.some(ps => ps.partnerName === data)
          );
          if (publisher) {
            this.selectedPublisherId = publisher.publisher;
          }
        }
      } else {
        // For non-admin users, we don't need to set publisherId since they don't have publisher filtering
        // The partner name itself will be used for token generation
        this.selectedPublisherId = '';
      }

      this.cdr.markForCheck();
    }
  }

  getSelectedPublisherLabel(): string {
    if (!this.selectedPublisher) return this.translateService.instant('All Publisher');
    const publisher = this.publishers.find(p => p.publisher === this.selectedPublisher);
    return publisher ? publisher.publisherName : this.translateService.instant('All Publisher');
  }

  getSelectedPartnerNameLabel(): string {
    const selectedPartner = this.tokenForm.get('partnerName')?.value;
    if (!selectedPartner) return this.isAdmin ? this.translateService.instant('All Partner Names') : this.translateService.instant('All Partners');

    // Since we're now storing the actual partnerName/publisherName, we can return it directly
    return selectedPartner;
  }

  getDefaultBackRoute(): string {
    return this.navigationService.getDefaultBackRoute();
  }

  handleBackNavigation(event: Event) {
    this.navigationService.handleBackNavigation(event);
  }

} 